/**
 * Domain types for equip-locations-finder
 *
 * Shared between client and server. Ported from playwright-parks-finder
 * with adaptations for the multi-user Supabase architecture.
 */

// =============================================================================
// Core Geo Types
// =============================================================================

export interface BoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface Coordinates {
  lat: number;
  lng: number;
}

// =============================================================================
// Enums
// =============================================================================

export const SCAN_STATUSES = [
  "pending",
  "keyword_scanning",
  "scanning",
  "screenshotting",
  "classifying",
  "website_classifying",
  "web_researching",
  "completed",
  "stopped",
  "failed",
] as const;
export type ScanStatus = (typeof SCAN_STATUSES)[number];

export const CELL_STATUSES = [
  "pending",
  "scanning",
  "completed",
  "subdivided",
  "failed",
] as const;
export type CellStatus = (typeof CELL_STATUSES)[number];

export const CLASSIFICATION_SOURCES = [
  "satellite",
  "website",
  "web_research",
] as const;
export type ClassificationSource = (typeof CLASSIFICATION_SOURCES)[number];

export const OWNERSHIP_TYPES = [
  "public",
  "private",
  "institutional",
  "unknown",
] as const;
export type OwnershipType = (typeof OWNERSHIP_TYPES)[number];

export const AI_MODELS = ["opus", "sonnet", "haiku"] as const;
export type AIModel = (typeof AI_MODELS)[number];

// =============================================================================
// Scan Config
// =============================================================================

export interface ScanConfigSettings {
  classificationModel: AIModel;
  websiteClassificationModel: AIModel;
  webResearchModel: AIModel;
  maxDepth: number;
  minCellArea: number;
  concurrency: number;
  screenshotZoom: number;
  rateLimit: number;
  languageCode: string;
  regionCode: string;
}

export const DEFAULT_SCAN_CONFIG_SETTINGS: ScanConfigSettings = {
  classificationModel: "sonnet",
  websiteClassificationModel: "haiku",
  webResearchModel: "sonnet",
  maxDepth: 8,
  minCellArea: 10_000,
  concurrency: 10,
  screenshotZoom: 19,
  rateLimit: 600,
  languageCode: "en",
  regionCode: "",
};

export interface ScanConfig {
  id: string;
  userId: string;
  name: string;
  city: string;
  bbox: BoundingBox | null;
  includedTypes: string[];
  classificationTypes: string[];
  settings: ScanConfigSettings;
  excludeInstitutions: boolean;
  excludePrivate: boolean;
  excludeNoSportField: boolean;
  sponsorName: string | null;
  polygon: { coordinates: number[][][] } | null;
  customPrompts: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Scan
// =============================================================================

export interface ScanProgress {
  cellsDone: number;
  cellsTotal: number;
  locationsFound: number;
  currentPhase: string;
}

export interface ScanStats {
  apiCalls: number;
  estimatedCost: number;
  subdivisions: number;
  screenshotsDone: number;
  screenshotsTotal: number;
  classificationsDone: number;
  classificationsTotal: number;
  websiteClassificationsDone: number;
  websiteClassificationsTotal: number;
}

export interface Scan {
  id: string;
  userId: string;
  configId: string | null;
  status: ScanStatus;
  currentPhase: string | null;
  progress: ScanProgress;
  stats: ScanStats;
  totalLocations: number;
  totalCost: number;
  sportFieldsFound: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Scan Cell
// =============================================================================

export interface ScanCell {
  id: string;
  scanId: string;
  cellId: string;
  bbox: BoundingBox;
  depth: number;
  status: CellStatus;
  resultCount: number;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Location
// =============================================================================

export interface Location {
  id: string;
  scanId: string | null;
  placeId: string;
  name: string;
  primaryType: string | null;
  types: string[];
  rating: number | null;
  userRatingCount: number | null;
  address: string | null;
  phone: string | null;
  website: string | null;
  hours: string | null;
  lat: number;
  lng: number;
  googleMapsUrl: string | null;
  photoRefs: string[];
  businessStatus: string | null;
  ownership: OwnershipType;
  ownershipReason: string | null;
  userNotes: string | null;
  isRejected: boolean;
  isReviewed: boolean;
  source: LocationSource;
  approvedByCity: boolean;
  approvedBySponsor: boolean;
  isCulturalSpot: boolean;
  visibilityRating: number | null;
  ownershipConfirmed: boolean;
  discoveryMethod: string;
  mergeSourceIds: unknown | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Screenshot
// =============================================================================

export interface Screenshot {
  id: string;
  locationId: string;
  storagePath: string;
  zoomLevel: number;
  width: number | null;
  height: number | null;
  isLowQuality: boolean;
  capturedAt: string;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Classification
// =============================================================================

export interface Classification {
  id: string;
  locationId: string;
  source: ClassificationSource;
  hasSportField: boolean | null;
  confidence: number | null;
  notes: string | null;
  modelUsed: string | null;
  costUsd: number | null;
  structuredData: StructuredClassificationData | null;
  // User overrides
  userHasSportField: boolean | null;
  userNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Structured Classification Quick-Facts (shared across all sources)
// =============================================================================

export interface StructuredFlag {
  text: string; // 3-5 words max
  type: "keep" | "reject";
}

export interface DetectedSport {
  type: string; // from configured list OR "other:description"
  certainty: "definite" | "possible";
}

export interface StructuredClassificationData {
  sports: DetectedSport[];
  flags: StructuredFlag[];
}

// =============================================================================
// Location Source
// =============================================================================

export const LOCATION_SOURCES = [
  "equip_scout",
  "city",
  "external_import",
] as const;
export type LocationSource = (typeof LOCATION_SOURCES)[number];

// =============================================================================
// Sport Court
// =============================================================================

export interface SportCourt {
  id: string;
  locationId: string;
  sportType: string;
  lat: number;
  lng: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Scan Request
// =============================================================================

export const SCAN_REQUEST_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "running",
  "completed",
] as const;
export type ScanRequestStatus = (typeof SCAN_REQUEST_STATUSES)[number];

export interface ScanRequest {
  id: string;
  userId: string;
  name: string | null;
  polygon: unknown;
  requestedVariables: unknown;
  status: ScanRequestStatus;
  scanId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Location with related data (for detail views)
// =============================================================================

export interface LocationDetail extends Location {
  screenshot: Screenshot | null;
  classifications: Classification[];
}

// =============================================================================
// Default Included Types (Google Places API types)
// =============================================================================

export const DEFAULT_INCLUDED_TYPES = [
  "park",
  "city_park",
  "sports_complex",
  "athletic_field",
  "sports_activity_location",
  "tennis_court",
  "sports_club",
  // "stadium",
  // "sports_club",
] as const;

// =============================================================================
// Default Classification Types (23 sport types)
// =============================================================================

export const DEFAULT_CLASSIFICATION_TYPES = [
  "soccer_pitch",
  "tennis_court",
  "padel_court",
  "basketball_court",
  "volleyball_court",
  "running_track",
  "baseball_diamond",
  "rugby_pitch",
  "field_hockey_pitch",
  "cricket_pitch",
  "skateboard_park",
  "multi_sport_court",
  "beach_volleyball_court",
  "petanque_court",
  "street_workout_area",
  "pickleball_court",
  "ping_pong_table",
  "american_football_field",
  "netball_court",
  "handball_court",
  "ice_hockey_rink",
  "ultimate_frisbee_field",
  "disc_golf_course",
] as const;

// =============================================================================
// Progress Stats (for real-time updates)
// =============================================================================

export interface ScanProgressStats {
  cellsDone: number;
  cellsQueued: number;
  placesFound: number;
  apiCalls: number;
  estimatedCost: number;
  concurrentRequests: number;
  maxConcurrency: number;
  subdivisions: number;
  elapsedMs: number;
}

export interface ScreenshotProgressStats {
  current: number;
  total: number;
  completed: number;
  failed: number;
  skipped: number;
  activeCount: number;
  currentName: string | null;
  etaMs: number | null;
}

export interface ClassificationProgressStats {
  current: number;
  total: number;
  completed: number;
  failed: number;
  skipped: number;
  activeCount: number;
  sportFieldsFound: number;
  currentName: string | null;
  etaMs: number | null;
}

export interface WebsiteClassifyProgressStats {
  current: number;
  total: number;
  completed: number;
  failed: number;
  skipped: number;
  activeCount: number;
  currentName: string | null;
  etaMs: number | null;
}

export interface WebResearchProgressStats {
  current: number;
  total: number;
  completed: number;
  failed: number;
  skipped: number;
  activeCount: number;
  currentName: string | null;
  etaMs: number | null;
}

// =============================================================================
// Presets
// =============================================================================

export interface ScanConfigPreset {
  name: string;
  description: string;
  includedTypes: readonly string[];
  classificationTypes: readonly string[];
  settings: Partial<ScanConfigSettings>;
}

// =============================================================================
// Collections
// =============================================================================

export const COLLECTION_COLORS = [
  "#3b82f6", // blue
  "#ef4444", // red
  "#22c55e", // green
  "#f59e0b", // amber
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#f97316", // orange
  "#14b8a6", // teal
  "#6366f1", // indigo
  "#a855f7", // purple
  "#84cc16", // lime
] as const;

export type CollectionColor = (typeof COLLECTION_COLORS)[number];

export type CollectionSyncStatus = "not_synced" | "synced" | "partially_synced";

export interface Collection {
  id: string;
  userId: string;
  scanConfigId: string | null;
  name: string;
  description: string | null;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface CollectionWithCount extends Collection {
  locationCount: number;
}

// =============================================================================
// Presets
// =============================================================================

export const SCAN_CONFIG_PRESETS: Record<string, ScanConfigPreset> = {
  sports_facilities: {
    name: "Sports Facilities",
    description: "Find sports fields, courts, and athletic facilities",
    includedTypes: DEFAULT_INCLUDED_TYPES,
    classificationTypes: DEFAULT_CLASSIFICATION_TYPES,
    settings: {
      screenshotZoom: 20,
      maxDepth: 8,
      minCellArea: 10_000,
    },
  },
  parks_recreation: {
    name: "Parks & Recreation",
    description: "Find parks, playgrounds, and recreational areas",
    includedTypes: ["park", "national_park", "state_park"],
    classificationTypes: DEFAULT_CLASSIFICATION_TYPES,
    settings: {
      screenshotZoom: 18,
      maxDepth: 6,
      minCellArea: 25_000,
    },
  },
};
